import { useEffect, useMemo, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import {
  ArrowLeft, Clock, Dumbbell, ChevronDown, Smile, Battery, MessageSquare, AlertCircle, CheckCircle, Flame,
  ClipboardList, Pencil, Plus, FileText,
} from "lucide-react"
import {
  listSessionFeedbacks, listFreeSessions, getTrainingLoads, updateCalendarEvent,
  getActivityEndAt, getActivityPostSessionAnswer, isSuiviSeul,
  type CalendarEventRow, type SessionFeedbackRow, type SessionMeta, type TrainingLoadRow,
} from "@/lib/supabase"
import { toLocalISODate } from "@/lib/utils"
import { computeTrainingLoad } from "@/lib/monitoring/calculations"
import { useAuth } from "@/contexts/AuthContext"
import { useSubscription } from "@/hooks/useSubscription"
import { BottomNav } from "@/components/BottomNav"
import { AddFreeSessionSheet, FREE_SESSION_CONTENT_MAX } from "@/components/calendar/AddEventFlow"

interface Props { email: string; firstName?: string }

type FilterKey = "all" | "week" | "month"

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "Toutes" },
  { key: "week", label: "Cette semaine" },
  { key: "month", label: "Ce mois" },
]

function formatDate(iso: string | Date): string {
  const d = new Date(iso)
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "long" })
}

// Une entrée de l'historique : séance du programme (`session_feedback`) ou séance libre
// déclarée au calendrier (`calendar_events`, type seance-libre). Les deux sources sont
// fusionnées et triées par moment de réalisation.
type HistoryItem =
  | { kind: "programme"; id: string; at: Date; row: SessionFeedbackRow }
  | { kind: "libre"; id: string; at: Date; event: CalendarEventRow }

/** Moment d'une séance libre : sa fin prévue, ou midi du jour sans heure de fin. */
function freeSessionAt(e: CalendarEventRow): Date {
  const endAt = getActivityEndAt(e)
  if (endAt) return endAt
  const [y, m, d] = e.event_date.split("-").map(Number)
  return new Date(y, m - 1, d, 12, 0)
}

const FREE_COLOR = "#a78bfa" // même violet que la séance libre du calendrier

// 4 paliers — mêmes teintes que l'échelle Hooper/ACWR/Strain (Monitoring, Dashboard) :
// vert → cyan → jaune → rouge. Sert de repère visuel en scrollant l'historique.
function rpeColor(rpe: number): { bg: string; fg: string } {
  if (rpe <= 4) return { bg: "rgba(34,197,94,0.14)",  fg: "#22c55e" }  // léger
  if (rpe <= 6) return { bg: "rgba(6,182,212,0.14)",  fg: "#06b6d4" }  // modéré
  if (rpe <= 8) return { bg: "rgba(234,179,8,0.14)",  fg: "#eab308" }  // difficile
  return         { bg: "rgba(239,68,68,0.14)",  fg: "#ef4444" }        // maximal
}

function enjoymentColor(e: number): string {
  if (e >= 8) return "#22c55e"
  if (e >= 5) return "#f97316"
  return "#ef4444"
}

// Semaine Lun→Dim, mois calendaire — cohérent avec CalendarPage
function isSameWeek(d: Date, ref: Date): boolean {
  const refDay = (ref.getDay() + 6) % 7 // 0 = lundi
  const monday = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() - refDay, 0, 0, 0, 0)
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6, 23, 59, 59, 999)
  return d >= monday && d <= sunday
}
function isSameMonth(d: Date, ref: Date): boolean {
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth()
}

// ── Filtre "Toutes / Cette semaine / Ce mois" ──
function FilterPills({ value, onChange }: { value: FilterKey; onChange: (k: FilterKey) => void }) {
  return (
    <div className="flex items-center gap-1.5 px-5 mb-4 overflow-x-auto scrollbar-hide relative z-10">
      {FILTERS.map((f) => {
        const active = f.key === value
        return (
          <button
            key={f.key}
            onClick={() => onChange(f.key)}
            className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap cursor-pointer transition-all active:scale-[0.97] ${active ? "text-cyan-400" : "text-gray-500"}`}
            style={active
              ? { background: "linear-gradient(135deg, rgba(3,105,161,0.35), rgba(6,182,212,0.25))", border: "1px solid rgba(6,182,212,0.5)", boxShadow: "0 4px 14px rgba(6,182,212,0.25)" }
              : { background: "rgba(15,22,35,0.6)", border: "1px solid rgba(31,41,55,0.6)" }}
          >
            {f.label}
          </button>
        )
      })}
    </div>
  )
}

// ── Liste des exercices dépliée : séries/reps/poids réels, avec repli sur les noms seuls ──
function ExerciseRows({ meta }: { meta: SessionMeta | null | undefined }) {
  if (!meta) return null

  if (meta.exercises && meta.exercises.length > 0) {
    return (
      <div className="flex flex-col gap-1 mb-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
          Exercices · {meta.exercises.length}
        </p>
        {meta.exercises.map((ex, i) => (
          <div key={i} className="flex flex-col gap-0.5 py-1">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" style={{ boxShadow: "0 0 5px rgba(6,182,212,0.7)" }} />
              <span className="text-[12.5px] text-gray-200 font-medium">{ex.name}</span>
            </div>
            <span className="text-[11.5px] text-gray-500 pl-3.5 tabular-nums">
              <span className="text-gray-100 font-bold">{ex.sets}</span>×<span className="text-gray-100 font-bold">{ex.reps}</span>
              {ex.weight && <span className="text-cyan-400 font-semibold ml-1.5">{ex.weight}</span>}
            </span>
          </div>
        ))}
      </div>
    )
  }

  if (meta.exerciseNames && meta.exerciseNames.length > 0) {
    return (
      <div className="flex flex-col gap-1.5 mb-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-0.5">Exercices</p>
        {meta.exerciseNames.map((name, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-gray-600 shrink-0" />
            <span className="text-[13px] text-gray-300">{name}</span>
          </div>
        ))}
      </div>
    )
  }

  return null
}

// ── Card État 1 : séance terminée, questionnaire en attente ──
function CardPending({ s, num, isOpen, onToggle }: {
  s: SessionFeedbackRow; num: number; isOpen: boolean; onToggle: () => void
}) {
  const meta = s.session_meta as SessionMeta | null
  const sessionNum = s.programme_session_number ?? num
  // Questionnaire non répondu depuis >3 jours → halo escaladé au rouge (même logique
  // caution→risque que les zones ACWR/Strain), pour repérer les relances prioritaires.
  const daysSince = (Date.now() - new Date(s.completed_at).getTime()) / 86_400_000
  const staleColor = daysSince > 3 ? "#ef4444" : "#f97316"

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden relative"
      style={{ background: "rgba(17,24,39,0.70)", border: `1px solid ${staleColor}33` }}
    >
      <div className="absolute -top-10 -right-10 w-[140px] h-[140px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${staleColor}2e, transparent 70%)` }} />

      <button onClick={onToggle} className="w-full text-left px-4 pt-4 pb-3 cursor-pointer transition-all active:scale-[0.99] relative z-10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.25)", color: "#06b6d4" }}>
            Séance {sessionNum}
          </span>
          <span className="text-[11px] text-gray-500">{formatDate(s.completed_at)}</span>
        </div>

        <h3 className="text-[17px] font-bold text-white mt-1 mb-1">
          {meta?.displayName ?? "Séance complétée"}
        </h3>

        <div className="flex items-center gap-2 flex-wrap mt-1 mb-2">
          {meta?.method && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.20)", color: "#06b6d4" }}>
              {meta.method}
            </span>
          )}
          {meta?.estimatedDuration && (
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <Clock className="w-3 h-3" /><span className="text-gray-600">Prévue</span> {meta.estimatedDuration}
            </span>
          )}
          {meta && (
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <Dumbbell className="w-3 h-3" />{meta.exerciseCount} ex · {meta.totalSets} séries
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" style={{ color: staleColor }} />
          <span className="text-[12px] font-semibold" style={{ color: staleColor }}>Questionnaire en attente</span>
        </div>

        <div className="flex justify-center mt-2">
          <ChevronDown className="w-4 h-4 text-gray-600 transition-transform duration-200"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
        </div>
      </button>

      <AnimatePresence>
        {isOpen && meta && (
          <motion.div initial={{ height: 0, opacity: 0, y: -10 }} animate={{ height: "auto", opacity: 1, y: 0 }}
            exit={{ height: 0, opacity: 0, y: -10 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden relative z-10">
            <div className="px-4 pb-4 pt-3 flex flex-col gap-1.5"
              style={{ borderTop: "1px solid rgba(30,41,59,0.3)" }}>
              <ExerciseRows meta={meta} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// ── Card État 2 : séance terminée + questionnaire rempli ──
function CardComplete({ s, num, isOpen, onToggle, trainingLoad }: {
  s: SessionFeedbackRow; num: number; isOpen: boolean; onToggle: () => void; trainingLoad: number | null
}) {
  const meta = s.session_meta as SessionMeta | null
  const sessionNum = s.programme_session_number ?? num
  const rpe = rpeColor(s.rpe ?? 0)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden relative"
      style={{ background: "rgba(17,24,39,0.70)", border: "1px solid rgba(30,41,59,0.60)" }}
    >
      <div className="absolute -top-10 -right-10 w-[140px] h-[140px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${rpe.fg}2e, transparent 70%)` }} />

      <button onClick={onToggle} className="w-full text-left px-4 pt-4 pb-3 cursor-pointer transition-all active:scale-[0.99] relative z-10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.25)", color: "#06b6d4" }}>
            Séance {sessionNum}
          </span>
          <span className="text-[11px] text-gray-500">{formatDate(s.completed_at)}</span>
        </div>

        <h3 className="text-[17px] font-bold text-white mt-1 mb-1">
          {meta?.displayName ?? "Séance complétée"}
        </h3>

        <div className="flex items-center gap-2 flex-wrap mt-1 mb-2">
          {meta?.method && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.20)", color: "#06b6d4" }}>
              {meta.method}
            </span>
          )}
          {meta?.estimatedDuration && (
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <Clock className="w-3 h-3" /><span className="text-gray-600">Prévue</span> {meta.estimatedDuration}
            </span>
          )}
          {meta && (
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <Dumbbell className="w-3 h-3" />{meta.exerciseCount} ex · {meta.totalSets} séries
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap mb-2">
          {s.actual_duration_minutes != null && (
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-[12px] text-gray-400 font-medium"><span className="text-gray-600 font-normal">Réalisée</span> {s.actual_duration_minutes} min</span>
            </div>
          )}
          {s.enjoyment != null && (
            <div className="flex items-center gap-1.5">
              <Smile className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-[12px] font-medium" style={{ color: enjoymentColor(s.enjoyment ?? 0) }}>
                Plaisir {s.enjoyment}/10
              </span>
            </div>
          )}
          {s.rpe != null && (
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full ml-auto"
              style={{ background: rpe.bg, border: `1px solid ${rpe.fg}55`, color: rpe.fg, textShadow: `0 0 10px ${rpe.fg}80` }}>
              RPE {s.rpe}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <span className="text-[12px] font-semibold text-emerald-400">Feedback enregistré</span>
        </div>

        <div className="flex justify-center mt-2">
          <ChevronDown className="w-4 h-4 text-gray-600 transition-transform duration-200"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ height: 0, opacity: 0, y: -10 }} animate={{ height: "auto", opacity: 1, y: 0 }}
            exit={{ height: 0, opacity: 0, y: -10 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden relative z-10">
            <div className="px-4 pb-4 pt-3 flex flex-col gap-2.5"
              style={{ borderTop: "1px solid rgba(30,41,59,0.3)" }}>

              <ExerciseRows meta={meta} />

              <div className="flex items-center justify-between mt-1 pt-2.5" style={{ borderTop: "1px solid rgba(30,41,59,0.3)" }}>
                <div className="flex items-center gap-2.5">
                  <Clock className="w-4 h-4 text-gray-500 shrink-0" />
                  <span className="text-[12px] text-gray-400">
                    Terminée à {new Date(s.session_meta?.end_time ?? s.completed_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                {trainingLoad != null && (
                  <div className="flex items-center gap-1.5">
                    <Flame className="w-3.5 h-3.5 text-orange-400" fill="#f97316" />
                    <span className="text-[12px] text-gray-400">
                      <span className="text-gray-100 font-bold tabular-nums" style={{ textShadow: "0 0 10px rgba(249,115,22,0.6)" }}>{trainingLoad}</span> UA
                    </span>
                  </div>
                )}
              </div>

              {s.fatigue != null && (
                <div className="flex items-center gap-2.5">
                  <Battery className="w-4 h-4 text-gray-500 shrink-0" />
                  <span className="text-[12px] text-gray-400">
                    Fatigue ressentie : <span className="text-gray-200 font-semibold">{s.fatigue}/10</span>
                  </span>
                </div>
              )}

              {s.notes && (
                <div className="flex items-start gap-2.5 mt-1">
                  <MessageSquare className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
                  <p className="text-[12px] text-gray-300 italic leading-relaxed">"{s.notes}"</p>
                </div>
              )}

              {!s.fatigue && !s.notes && !meta?.exerciseNames?.length && (
                <p className="text-[11px] text-gray-600 italic">Pas de détails supplémentaires.</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// ── Card séance libre : titre, date, durée/RPE si questionnaire rempli, contenu ──
// Même présentation que les séances du programme, sans exercices ni charges.
function CardFreeSession({ event, isOpen, onToggle, canEdit, onContentSaved }: {
  event: CalendarEventRow; isOpen: boolean; onToggle: () => void; canEdit: boolean
  onContentSaved: (updated: CalendarEventRow) => void
}) {
  const navigate = useNavigate()
  const answer = getActivityPostSessionAnswer(event)
  const done = answer?.status === "done" ? answer : null
  const at = freeSessionAt(event)
  const rpe = done ? rpeColor(done.rpe) : null
  // Questionnaire non répondu depuis >3 jours → rouge, comme les séances du programme.
  const daysSince = (Date.now() - at.getTime()) / 86_400_000
  const staleColor = daysSince > 3 ? "#ef4444" : "#f97316"
  const accent = rpe?.fg ?? staleColor

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(event.content ?? "")
  const [saving, setSaving] = useState(false)

  const startEdit = () => { setDraft(event.content ?? ""); setEditing(true) }
  const saveContent = async () => {
    setSaving(true)
    try {
      const trimmed = draft.trim()
      // Colonne dédiée : n'écrase jamais `meta` (réponse au questionnaire).
      const updated = await updateCalendarEvent(event.id, { content: trimmed ? trimmed : null })
      onContentSaved(updated)
      setEditing(false)
      toast.success("Contenu enregistré")
    } catch (err) {
      toast.error("Erreur", { description: err instanceof Error ? err.message : undefined })
    } finally {
      setSaving(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden relative"
      style={{ background: "rgba(17,24,39,0.70)", border: done ? "1px solid rgba(30,41,59,0.60)" : `1px solid ${staleColor}33` }}
    >
      <div className="absolute -top-10 -right-10 w-[140px] h-[140px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accent}2e, transparent 70%)` }} />

      <button onClick={onToggle} className="w-full text-left px-4 pt-4 pb-3 cursor-pointer transition-all active:scale-[0.99] relative z-10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: "rgba(139,92,246,0.14)", border: "1px solid rgba(139,92,246,0.35)", color: FREE_COLOR }}>
            Séance libre
          </span>
          <span className="text-[11px] text-gray-500">{formatDate(at)}</span>
        </div>

        <h3 className="text-[17px] font-bold text-white mt-1 mb-1 break-words">{event.title}</h3>

        {event.content && !isOpen && (
          <p className="text-[12px] text-gray-400 leading-relaxed line-clamp-2 whitespace-pre-line break-words mb-2">{event.content}</p>
        )}

        {done && rpe && (
          <div className="flex items-center gap-3 flex-wrap mb-2 mt-1">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-[12px] text-gray-400 font-medium"><span className="text-gray-600 font-normal">Réalisée</span> {done.duration} min</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Smile className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-[12px] font-medium" style={{ color: enjoymentColor(done.pleasure) }}>
                Plaisir {done.pleasure}/10
              </span>
            </div>
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full ml-auto"
              style={{ background: rpe.bg, border: `1px solid ${rpe.fg}55`, color: rpe.fg, textShadow: `0 0 10px ${rpe.fg}80` }}>
              RPE {done.rpe}
            </span>
          </div>
        )}

        {done ? (
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span className="text-[12px] font-semibold text-emerald-400">Feedback enregistré</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" style={{ color: staleColor }} />
            <span className="text-[12px] font-semibold" style={{ color: staleColor }}>Questionnaire en attente</span>
          </div>
        )}

        <div className="flex justify-center mt-2">
          <ChevronDown className="w-4 h-4 text-gray-600 transition-transform duration-200"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ height: 0, opacity: 0, y: -10 }} animate={{ height: "auto", opacity: 1, y: 0 }}
            exit={{ height: 0, opacity: 0, y: -10 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden relative z-10">
            <div className="px-4 pb-4 pt-3 flex flex-col gap-2.5"
              style={{ borderTop: "1px solid rgba(30,41,59,0.3)" }}>

              {/* CONTENU — facultatif, modifiable après coup */}
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Contenu de la séance</p>
                {canEdit && !editing && (
                  <button onClick={startEdit}
                    className="flex items-center gap-1 text-[11px] font-semibold cursor-pointer transition-opacity active:opacity-70"
                    style={{ color: FREE_COLOR }}>
                    {event.content ? <Pencil className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    {event.content ? "Modifier" : "Ajouter"}
                  </button>
                )}
              </div>

              {editing ? (
                <>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={"Ex :\nSquat 4×8 à 80 kg\nTractions 3×10"}
                    rows={6}
                    maxLength={FREE_SESSION_CONTENT_MAX}
                    autoFocus
                    className="w-full px-3 py-2.5 rounded-xl outline-none text-[13px] text-white placeholder:text-gray-600 resize-none leading-relaxed"
                    style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)", caretColor: "#06b6d4" }}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setEditing(false)} disabled={saving}
                      className="py-2.5 rounded-lg text-[12px] font-semibold text-gray-400 cursor-pointer disabled:opacity-50"
                      style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
                      Annuler
                    </button>
                    <button onClick={saveContent} disabled={saving}
                      className="py-2.5 rounded-lg text-[12px] font-bold text-white cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2"
                      style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)" }}>
                      {saving && <div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
                      {saving ? "…" : "Enregistrer"}
                    </button>
                  </div>
                </>
              ) : event.content ? (
                <div className="flex items-start gap-2.5">
                  <FileText className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
                  <p className="text-[12.5px] text-gray-300 leading-relaxed whitespace-pre-line break-words min-w-0">{event.content}</p>
                </div>
              ) : (
                <p className="text-[11px] text-gray-600 italic">Aucun contenu renseigné.</p>
              )}

              <div className="flex items-center justify-between mt-1 pt-2.5" style={{ borderTop: "1px solid rgba(30,41,59,0.3)" }}>
                <div className="flex items-center gap-2.5">
                  <Clock className="w-4 h-4 text-gray-500 shrink-0" />
                  <span className="text-[12px] text-gray-400">
                    {event.end_time
                      ? <>Terminée à {at.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</>
                      : formatDate(at)}
                  </span>
                </div>
                {done && (
                  <div className="flex items-center gap-1.5">
                    <Flame className="w-3.5 h-3.5 text-orange-400" fill="#f97316" />
                    <span className="text-[12px] text-gray-400">
                      <span className="text-gray-100 font-bold tabular-nums" style={{ textShadow: "0 0 10px rgba(249,115,22,0.6)" }}>{done.training_load}</span> UA
                    </span>
                  </div>
                )}
              </div>

              {!answer && (
                <button onClick={() => navigate(`/post-session?event=${event.id}`)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 mt-1 rounded-lg cursor-pointer text-[12px] font-bold text-white"
                  style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)" }}>
                  <ClipboardList className="w-3.5 h-3.5" />
                  Remplir le questionnaire
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export const SessionsHistoryPage = ({}: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { wizardData } = useAuth()
  const suiviSeul = isSuiviSeul(wizardData)
  // Même règle que le calendrier : écriture réservée à un accès actif.
  const { isActive } = useSubscription()
  const [sessions, setSessions] = useState<SessionFeedbackRow[]>([])
  const [freeSessions, setFreeSessions] = useState<CalendarEventRow[]>([])
  const [loads, setLoads] = useState<TrainingLoadRow[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterKey>("all")
  const [addOpen, setAddOpen] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancel = false
    setLoading(true)
    ;(async () => {
      try {
        // Séances libres lues à part : un échec de leur côté ne doit pas vider
        // l'historique des séances du programme.
        const [rows, loadRows, freeRows] = await Promise.all([
          listSessionFeedbacks(50),
          getTrainingLoads(400),
          listFreeSessions(50).catch((e) => {
            console.error("[SessionsHistoryPage] séances libres :", e)
            return [] as CalendarEventRow[]
          }),
        ])
        if (!cancel) { setSessions(rows); setLoads(loadRows); setFreeSessions(freeRows) }
      } catch (e) {
        console.error("[SessionsHistoryPage] fetch error:", e)
      } finally {
        if (!cancel) setLoading(false)
      }
    })()
    return () => { cancel = true }
  }, [location.key, reloadKey])

  const items = useMemo<HistoryItem[]>(() => {
    const all: HistoryItem[] = [
      ...sessions.map((row) => ({ kind: "programme" as const, id: row.id, at: new Date(row.completed_at), row })),
      ...freeSessions.map((event) => ({ kind: "libre" as const, id: event.id, at: freeSessionAt(event), event })),
    ]
    return all.sort((a, b) => b.at.getTime() - a.at.getTime())
  }, [sessions, freeSessions])

  const replaceFreeSession = (updated: CalendarEventRow) =>
    setFreeSessions((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))

  // Charge du JOUR ("UA") indexée par date locale — training_load est unique par (user, date).
  // Ne sert plus que de repli (voir sessionLoad) : depuis le 14/10, cette ligne additionne
  // les séances et les activités du calendrier du même jour.
  const loadByDate = useMemo(() => {
    const map: Record<string, number> = {}
    for (const l of loads) map[l.date] = l.training_load
    return map
  }, [loads])

  const filtered = useMemo(() => {
    if (filter === "all") return items
    const now = new Date()
    return items.filter((it) => (filter === "week" ? isSameWeek(it.at, now) : isSameMonth(it.at, now)))
  }, [items, filter])

  const toggle = (id: string) => setExpandedId(expandedId === id ? null : id)
  const total = filtered.length
  // Repli de numérotation des séances du programme sans numéro : rang parmi elles
  // seules, les séances libres n'entrent pas dans le compte.
  const programmeCount = filtered.filter((it) => it.kind === "programme").length
  let programmeRank = 0

  return (
    <div className="min-h-screen bg-[#060a13] pb-24 cursor-default relative">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: "radial-gradient(80% 50% at 70% 0%, rgba(6,182,212,0.10), transparent 60%), radial-gradient(60% 40% at 20% 100%, rgba(3,105,161,0.10), transparent 60%)" }} />
      </div>

      {/* HEADER */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-5 pt-5 pb-3 relative z-10">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="w-9 h-9 rounded-lg bg-gray-800/50 flex items-center justify-center hover:bg-gray-700/50 cursor-pointer">
              <ArrowLeft className="w-4 h-4 text-gray-400" />
            </button>
            <h1 className="text-[22px] font-bold text-white tracking-tight">Séances</h1>
          </div>
          {suiviSeul && isActive && (
            <button onClick={() => setAddOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-bold text-white cursor-pointer transition-transform active:scale-[0.97]"
              style={{ background: "linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)" }}>
              <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
              Ajouter
            </button>
          )}
        </div>
      </motion.div>

      {/* FILTRES */}
      <FilterPills value={filter} onChange={setFilter} />

      {/* SOUS-HEADER + BADGE TOTAL */}
      <div className="flex items-center justify-between px-5 mb-3 relative z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-[4px] h-[18px] rounded-full" style={{ background: "#06b6d4", boxShadow: "0 0 8px rgba(6,182,212,0.4)" }} />
          <h2 className="text-[15px] font-semibold text-gray-400 tracking-wide">Mon historique</h2>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ background: "rgba(6,182,212,0.10)", border: "1px solid rgba(6,182,212,0.20)" }}>
          <Dumbbell className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-[13px] font-bold text-cyan-400">{total}</span>
          <span className="text-[11px] text-gray-500">séance{total > 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="px-4 mt-6 flex flex-col items-center gap-2 relative z-10">
          <div className="w-6 h-6 rounded-full border-2 border-gray-700 border-t-cyan-400 animate-spin" />
          <p className="text-[12px] text-gray-500">Chargement…</p>
        </div>
      )}

      {/* EMPTY STATE — aucune séance du tout */}
      {!loading && items.length === 0 && (
        <div className="px-6 mt-12 flex flex-col items-center text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-gray-900/60 border border-gray-800/60 flex items-center justify-center mb-4">
            <Dumbbell className="w-7 h-7 text-gray-600" />
          </div>
          <p className="text-[15px] font-semibold text-gray-300">Aucune séance encore</p>
          <p className="text-[12px] text-gray-500 mt-1 max-w-[280px]">
            {suiviSeul
              ? "Ajoute ta première séance pour la retrouver ici, avec ta charge et ton ressenti."
              : "Termine ta première séance pour voir ton historique ici."}
          </p>
          {suiviSeul ? (
            isActive && (
              <button onClick={() => setAddOpen(true)}
                className="mt-5 px-4 py-2 rounded-xl text-[13px] font-bold text-white cursor-pointer transition-transform active:scale-[0.97]"
                style={{ background: "linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)" }}>
                Ajouter une séance
              </button>
            )
          ) : (
            <button onClick={() => navigate("/dashboard/session")}
              className="mt-5 px-4 py-2 rounded-xl text-[13px] font-bold text-white cursor-pointer transition-transform active:scale-[0.97]"
              style={{ background: "linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)" }}>
              Démarrer une séance
            </button>
          )}
        </div>
      )}

      {/* EMPTY STATE — filtre sans résultat */}
      {!loading && items.length > 0 && filtered.length === 0 && (
        <div className="px-6 mt-12 flex flex-col items-center text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-gray-900/60 border border-gray-800/60 flex items-center justify-center mb-4">
            <Dumbbell className="w-7 h-7 text-gray-600" />
          </div>
          <p className="text-[15px] font-semibold text-gray-300">Aucune séance sur cette période</p>
          <button onClick={() => setFilter("all")}
            className="mt-4 px-4 py-2 rounded-xl text-[13px] font-semibold text-cyan-400 cursor-pointer transition-all active:scale-[0.97]"
            style={{ background: "rgba(6,182,212,0.10)", border: "1px solid rgba(6,182,212,0.20)" }}>
            Voir toutes les séances
          </button>
        </div>
      )}

      {/* CARDS */}
      {!loading && filtered.length > 0 && (
        <div className="px-4 relative z-10 flex flex-col gap-3">
          {filtered.map((it) => {
            const isOpen = expandedId === it.id

            if (it.kind === "libre") {
              return (
                <CardFreeSession key={it.id} event={it.event} isOpen={isOpen} onToggle={() => toggle(it.id)}
                  canEdit={isActive} onContentSaved={replaceFreeSession} />
              )
            }

            const s = it.row
            const num = programmeCount - programmeRank++
            const isPending = s.rpe === null
            // Charge de CETTE séance : RPE × durée de son questionnaire. Repli sur la charge
            // du jour pour une ligne ancienne sans durée.
            const trainingLoad = s.rpe != null && s.actual_duration_minutes != null
              ? computeTrainingLoad(s.rpe, s.actual_duration_minutes)
              : loadByDate[toLocalISODate(new Date(s.completed_at))] ?? null

            return isPending
              ? <CardPending key={s.id} s={s} num={num} isOpen={isOpen} onToggle={() => toggle(s.id)} />
              : <CardComplete key={s.id} s={s} num={num} isOpen={isOpen} onToggle={() => toggle(s.id)} trainingLoad={trainingLoad} />
          })}
        </div>
      )}

      {suiviSeul && (
        <AddFreeSessionSheet
          open={addOpen}
          date={toLocalISODate(new Date())}
          onClose={() => setAddOpen(false)}
          onCreated={() => setReloadKey((k) => k + 1)}
        />
      )}

      <BottomNav />
    </div>
  )
}
